import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const PRIMARY = "hsl(var(--primary))";
const GRID = "hsl(var(--border))";
const TIP = { contentStyle: { background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }, cursor: { fill: "hsl(var(--secondary))" } };
const AX = { tick: { fill: "hsl(var(--muted-foreground))", fontSize: 11 }, stroke: GRID };

export const Panel = ({ title, children, testid, hint }) => (
  <div className="space-y-3 rounded-2xl border bg-card p-4" data-testid={testid}>
    <div>
      <h3 className="font-bold">{title}</h3>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
    {children}
  </div>
);

export const Empty = ({ t }) => <p className="py-8 text-center text-sm text-muted-foreground">{t("an.noData")}</p>;

export function TimelineChart({ data, t }) {
  return (
    <div dir="ltr" className="h-64">
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ left: -20, right: 8, top: 8 }}>
          <defs><linearGradient id="anv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={PRIMARY} stopOpacity={0.4} /><stop offset="100%" stopColor={PRIMARY} stopOpacity={0} /></linearGradient></defs>
          <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" {...AX} tickFormatter={(v) => v.slice(5)} minTickGap={20} />
          <YAxis allowDecimals={false} {...AX} />
          <Tooltip {...TIP} />
          <Area type="monotone" dataKey="visits" name={t("an.m.visits")} stroke={PRIMARY} fill="url(#anv)" strokeWidth={2} />
          <Area type="monotone" dataKey="dish_views" name={t("an.m.dish_views")} stroke="#a1a1aa" fill="transparent" strokeWidth={1.5} strokeDasharray="4 3" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ColumnChart({ data, dataKey = "value", label, height = 200 }) {
  return (
    <div dir="ltr" style={{ height }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ left: -20, right: 8, top: 8 }}>
          <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" {...AX} interval={0} />
          <YAxis allowDecimals={false} {...AX} />
          <Tooltip {...TIP} />
          <Bar dataKey={dataKey} name={label} fill={PRIMARY} radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function BarList({ rows, format = (v) => v, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((r) => (
        <div key={r.key} className="group space-y-1" title={`${r.name}: ${format(r.value)}`}>
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="truncate font-medium">{r.name}</span>
            <bdi className="shrink-0 tabular-nums text-muted-foreground">{format(r.value)}</bdi>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-secondary">
            <div className="h-full rounded-full bg-primary transition-[width] duration-500 group-hover:opacity-80" style={{ width: `${(100 * r.value) / max}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function LanguagePie({ en, ar, t }) {
  const data = [{ name: "English", value: en }, { name: "العربية", value: ar }];
  const total = en + ar;
  return (
    <div className="flex items-center gap-4">
      <div dir="ltr" className="h-40 w-40 shrink-0">
        <ResponsiveContainer>
          <PieChart>
            <Pie data={data} dataKey="value" innerRadius={42} outerRadius={70} paddingAngle={3} stroke="none">
              <Cell fill={PRIMARY} /><Cell fill="#a1a1aa" />
            </Pie>
            <Tooltip {...TIP} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-2 text-sm">
        {data.map((d, i) => (
          <div key={d.name} className="flex items-center gap-2" data-testid={`an-lang-${i ? "ar" : "en"}`}>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: i ? "#a1a1aa" : PRIMARY }} />
            <span>{d.name}</span>
            <bdi className="font-bold tabular-nums">{total ? Math.round((100 * d.value) / total) : 0}%</bdi>
            <span className="text-xs text-muted-foreground">({d.value} {t("an.sessionsShort")})</span>
          </div>
        ))}
      </div>
    </div>
  );
}
